import { Box, Flex, Text } from '@chakra-ui/react'
import React from 'react'

import toEnumValueOrNull from '@/utils/toEnumValueOrNull'

import { LearnerFeedbackAttributeTag } from '@/client/services/learnerFeedback/constants'

import AttributeTagButton from '@/components/LearnerFeedback/AttributeTagButton'
import { FormContext, type FormInputType } from '@/components/UI/Form/types'

const MAX_TAGS = 5

// Custom renderer for a 'multi-select' Form input using chip-style AttributeTagButton
// Expects input.options as [{ label, value }] where value is the tag string
// Writes a string[] to FormContext (up to MAX_TAGS items)
const DialoguesPeerTagsFormInput: React.FC<{
  input: FormInputType
}> = ({ input }) => {
  if (input.type !== 'multi-select') {
    throw new Error('Invariant violation')
  }
  const { name: inputName } = input
  const { submitting, onChange, errors } = React.useContext(FormContext)

  const optionValues = React.useMemo(() => input.options.map(o => o.value), [input.options])

  const [selected, setSelected] = React.useState<string[]>(() => input.initialValue ?? [])

  React.useEffect(() => {
    setSelected(input.initialValue ?? [])
  }, [input.initialValue])

  const handleToggle = React.useCallback(
    (tag: string) => {
      if (submitting) {
        return
      }
      setSelected(prev => {
        const exists = prev.includes(tag)
        const next = exists ? prev.filter(t => t !== tag) : [...prev, tag]
        // enforce max
        const bounded = next.length > MAX_TAGS ? prev : next
        onChange(inputName, bounded)
        return bounded
      })
    },
    [submitting, inputName, onChange]
  )

  return (
    <>
      <Flex
        gap="8px"
        align="center"
        justify={{ base: 'flex-start', md: 'flex-start' }}
        wrap="wrap"
        width="100%"
      >
        {optionValues.map(value => {
          const typedTag = toEnumValueOrNull(LearnerFeedbackAttributeTag, value)
          const isSelected = selected.includes(value)

          return typedTag == null ? null : (
            <Box key={value} flexShrink={0}>
              <AttributeTagButton
                tag={typedTag}
                isSelected={isSelected}
                disabledData={{
                  disabled: submitting || (selected.length >= MAX_TAGS && !isSelected),
                  tooltip: submitting
                    ? 'Please wait while form is submitting'
                    : `You cannot select more than ${MAX_TAGS} tags`,
                }}
                onToggle={() => handleToggle(value)}
                variant="icon-text"
              />
            </Box>
          )
        })}
      </Flex>
      {errors[inputName] != null ? (
        <Text color="fg.error" mt="4px" fontSize="sm">
          {errors[inputName]}
        </Text>
      ) : null}
    </>
  )
}

export default DialoguesPeerTagsFormInput
